import Big from "big.js";
import { useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency } from "medere-ui-framework";
import { BudgetDetailDTO, calculateDistributionPercentages } from "../../../dto/budget/BudgetDetailDTO";
import { BillableItemDTO } from "../../../dto/budget/BilliableItemDTO";
import { BillableItemStatus } from "../../../enums/BillableItemStatus";
import {
    BILL_ITEM_CATEGORY_CONFIG,
    BillItemCategory,
    findDistributionError,
    formatPercent,
    formatUnits,
    getDistributionState,
    getTotalUnits,
    recalculateItem,
    withAssignments,
    withBillableStatus,
    withCoverage,
} from "./billItems";
import NumberField from "./NumberField";
import UnitDistributionSection from "./UnitDistributionSection";
import { formatApplicationDate } from "./tableParts";

/** Estados de una unidad (lista fija del front). */
const UNIT_STATUS_OPTIONS = [
    { value: BillableItemStatus.FACTURABLE, label: 'Facturable' },
    { value: BillableItemStatus.FACTURABLE_BONIFICADO, label: 'Facturable bonificada' },
    { value: BillableItemStatus.NO_FACTURABLE, label: 'No facturable' },
];

const statusLabel = (status: number) => UNIT_STATUS_OPTIONS.find(o => o.value === status)?.label ?? '—';

interface ItemDetailDrawerProps {
    category: BillItemCategory;
    item: BudgetDetailDTO;
    onHide: () => void;
    /** Aplica los cambios a la factura (se guardan al guardar la facturación). No se usa en solo lectura. */
    onApply?: (item: BudgetDetailDTO) => void;
    onAppliedToAll?: (units: number) => void;
    /** Vista previa: muestra el detalle como datos, sin campos. */
    readOnly?: boolean;
}

type UnitRow = { rowKey: string; unit: BillableItemDTO; index: number };

/**
 * Panel lateral "Editar {ítem}" (o "Detalle" en solo lectura): reemplaza los modales de detalle, cobertura,
 * honorarios/derechos y distribución. Al editar trabaja sobre una copia; Cancelar la descarta.
 */
export default function ItemDetailDrawer({ category, item, onHide, onApply, onAppliedToAll, readOnly = false }: ItemDetailDrawerProps) {
    const config = BILL_ITEM_CATEGORY_CONFIG[category];
    const isPractice = category === 'professionalActivities';
    const [draft, setDraft] = useState<BudgetDetailDTO>(() => {
        const copy = structuredClone(item);
        // Sin honorarios ni derechos cargados (ítems traídos del período, convenio sin división), todo el valor
        // unitario se toma como honorarios, como hacía el diálogo anterior.
        if (isPractice && !copy.medicalamount && !copy.expensesamount && copy.unitprice) {
            const { medicalPercentage, expensesPercentage } = calculateDistributionPercentages(copy.unitprice, 0);
            copy.medicalamount = copy.unitprice;
            copy.medicalpercentage = medicalPercentage;
            copy.expensespercentage = expensesPercentage;
        }
        return copy;
    });
    const [openUnit, setOpenUnit] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const units = draft.billableitems ?? [];
    const totalUnits = getTotalUnits(draft);
    const patientPercentage = 100 - (draft.healthentitypercentagecoverage ?? 0);
    const title = readOnly
        ? `Detalle ${config.article === 'una' ? 'de la' : 'del'} ${config.itemLabel}`
        : `Editar ${config.itemLabel}`;

    const change = (next: BudgetDetailDTO) => {
        setDraft(next);
        setError(null);
    };

    /** Honorarios y derechos por separado: el valor unitario es la suma y se guarda la proporción. */
    const setSplit = (medicalamount: number, expensesamount: number) => {
        const { medicalPercentage, expensesPercentage } = calculateDistributionPercentages(medicalamount, expensesamount);
        change({
            ...draft,
            medicalamount,
            expensesamount,
            unitprice: new Big(medicalamount).plus(expensesamount).round(2, Big.roundHalfUp).toNumber(),
            medicalpercentage: medicalPercentage,
            expensespercentage: expensesPercentage,
        });
    };

    const apply = () => {
        if (isPractice) {
            const problem = findDistributionError(draft);
            if (problem) {
                setOpenUnit(problem.unitIndex);
                setError(problem.message);
                return;
            }
        }
        onApply?.(recalculateItem(category, draft));
    };

    const applyToAll = () => {
        if (openUnit === null) return;
        const source = units[openUnit]?.professionalassignmentdtolist ?? [];
        change({
            ...draft,
            billableitems: units.map((u, i) => i === openUnit ? u : { ...u, professionalassignmentdtolist: source.map(a => ({ ...a })) }),
        });
        onAppliedToAll?.(units.length - 1);
    };

    const unitRows: UnitRow[] = units.map((unit, index) => ({ rowKey: String(unit.itemid ?? `new-${index}`), unit, index }));

    const distributionText = (unit: BillableItemDTO) => {
        const state = getDistributionState(unit);
        if (state.kind === 'empty') return <span className="nv-status-warn">Sin asignar</span>;
        if (state.kind === 'complete') return <span className="nv-status-ok">100% · {state.professionals} {state.professionals === 1 ? 'profesional' : 'profesionales'}</span>;
        return <span className={state.total > 100 ? 'nv-status-err' : 'nv-status-warn'}>{formatPercent(state.total)}</span>;
    };

    return (
        <Sidebar
            visible
            position="right"
            onHide={onHide}
            showCloseIcon={false}
            blockScroll
            className="nv-scope nv-drawer"
            aria-labelledby="nv-drawer-title"
        >
            <div className="nv-drawer-layout">
                <header className="nv-drawer-head">
                    <div className="nv-stack-4">
                        <h2 className="nv-modal-title" id="nv-drawer-title">{title}</h2>
                        <span className="nv-subtitle">{draft.description}</span>
                        <span className="nv-muted">
                            {formatApplicationDate(draft.applicationdate)} · {formatUnits(totalUnits)} {totalUnits === 1 ? 'unidad' : 'unidades'}
                        </span>
                    </div>
                    <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times" aria-label="Cerrar" data-pr-tooltip="Cerrar" onClick={onHide} />
                </header>

                <div className="nv-drawer-body">
                    {isPractice && (
                        <section className="nv-section" aria-labelledby="nv-drawer-price">
                            <h3 className="nv-section-title" id="nv-drawer-price">Valor unitario</h3>
                            {readOnly ? (
                                <div className="nv-form-row">
                                    <div className="nv-ro nv-w-s">
                                        <span className="nv-ro-label">Honorarios</span>
                                        <span className="nv-ro-value">{formatCurrency(draft.medicalamount ?? 0)}</span>
                                    </div>
                                    <div className="nv-ro nv-w-s">
                                        <span className="nv-ro-label">Derechos</span>
                                        <span className="nv-ro-value">{formatCurrency(draft.expensesamount ?? 0)}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="nv-form-row">
                                    <div className="nv-field nv-w-s">
                                        <label htmlFor="nv-drawer-medical">Honorarios</label>
                                        <NumberField id="nv-drawer-medical" kind="currency" ariaLabel="Honorarios" value={draft.medicalamount} min={0}
                                            onCommit={(medical) => setSplit(medical, draft.expensesamount)} />
                                    </div>
                                    <div className="nv-field nv-w-s">
                                        <label htmlFor="nv-drawer-expenses">Derechos</label>
                                        <NumberField id="nv-drawer-expenses" kind="currency" ariaLabel="Derechos" value={draft.expensesamount} min={0}
                                            onCommit={(expenses) => setSplit(draft.medicalamount, expenses)} />
                                    </div>
                                </div>
                            )}
                            <div className="nv-ro-inline">
                                <span className="nv-ro-label">Valor unitario total</span>
                                <span className="nv-ro-value nv-strong">{formatCurrency(draft.unitprice)}</span>
                            </div>
                        </section>
                    )}

                    <section className="nv-section" aria-labelledby="nv-drawer-coverage">
                        <h3 className="nv-section-title" id="nv-drawer-coverage">Cobertura</h3>
                        <div className="nv-form-row">
                            {readOnly ? (
                                <div className="nv-ro nv-w-s">
                                    <span className="nv-ro-label">Obra social</span>
                                    <span className="nv-ro-value">{formatPercent(draft.healthentitypercentagecoverage ?? 0)}</span>
                                </div>
                            ) : (
                                <div className="nv-field nv-w-s">
                                    <label htmlFor="nv-drawer-os">Obra social</label>
                                    <NumberField id="nv-drawer-os" kind="percent" ariaLabel="Cobertura de la obra social" value={draft.healthentitypercentagecoverage ?? 0} min={0} max={100}
                                        onCommit={(value) => change(withCoverage(draft, value))} />
                                </div>
                            )}
                            <div className={readOnly ? 'nv-ro nv-w-s' : 'nv-ro nv-w-s nv-ro-aligned'}>
                                <span className="nv-ro-label">Paciente</span>
                                <span className="nv-ro-value">{formatPercent(patientPercentage)}</span>
                            </div>
                        </div>
                    </section>

                    {units.length > 0 && (
                        <section className="nv-section" aria-labelledby="nv-drawer-units">
                            <h3 className="nv-section-title" id="nv-drawer-units">Unidades</h3>
                            <DataTable value={unitRows} dataKey="rowKey" className="nv-compact nv-load-table" rowClassName={(row: UnitRow) => ({ 'nv-row-editing': row.index === openUnit })}>
                                <Column header="Unidad" body={(row: UnitRow) => `Unidad ${row.index + 1}`} />
                                <Column header="Cantidad" align="center" alignHeader="center" body={(row: UnitRow) => formatUnits(Number(row.unit.quantity) || 0)} />
                                <Column header="Estado" body={(row: UnitRow) => readOnly ? statusLabel(row.unit.status) : (
                                    <Dropdown
                                        value={row.unit.status}
                                        options={UNIT_STATUS_OPTIONS}
                                        onChange={(e) => change(withBillableStatus(draft, row.index, e.value))}
                                        aria-label={`Estado de la unidad ${row.index + 1}`}
                                        className="nv-in-cell nv-w-status"
                                    />
                                )} />
                                {isPractice && <Column header="Distribución" body={(row: UnitRow) => distributionText(row.unit)} />}
                                {isPractice && (
                                    <Column
                                        header={<span className="nv-sr-only">Acciones</span>}
                                        className="nv-col-actions"
                                        body={(row: UnitRow) => (
                                            <Button
                                                type="button"
                                                text
                                                className="nv-icon nv-tip"
                                                icon={readOnly ? 'pi pi-eye' : 'pi pi-pencil'}
                                                aria-label={`${readOnly ? 'Ver' : 'Editar'} distribución de la unidad ${row.index + 1}`}
                                                aria-pressed={row.index === openUnit}
                                                data-pr-tooltip={readOnly ? 'Ver distribución' : 'Editar distribución'}
                                                onClick={() => { setOpenUnit(row.index); setError(null); }}
                                            />
                                        )}
                                    />
                                )}
                            </DataTable>
                        </section>
                    )}

                    {isPractice && openUnit !== null && units[openUnit] && (
                        <UnitDistributionSection
                            key={openUnit}
                            unitLabel={`Unidad ${openUnit + 1}`}
                            assignments={units[openUnit].professionalassignmentdtolist ?? []}
                            medicalAmount={draft.medicalamount ?? 0}
                            error={error}
                            canApplyToAll={!readOnly && units.length > 1}
                            readOnly={readOnly}
                            onChange={(assignments) => change(withAssignments(draft, openUnit, assignments))}
                            onApplyToAll={applyToAll}
                            onClose={() => { setOpenUnit(null); setError(null); }}
                        />
                    )}
                </div>

                <footer className="nv-drawer-foot">
                    {readOnly ? (
                        <Button type="button" className="buttonMain2" outlined label="Cerrar" onClick={onHide} />
                    ) : (
                        <>
                            <span className="nv-muted nv-push-left">Se guarda al guardar la facturación</span>
                            <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onHide} />
                            <Button type="button" className="buttonMain" label="Aplicar cambios" onClick={apply} />
                        </>
                    )}
                </footer>
            </div>
        </Sidebar>
    );
}
